import { useCallback, useEffect, useState } from "react";
import {
	type Conversation,
	createNewConversation,
	deleteConversation,
	listConversations,
	loadConversation,
	saveConversation,
} from "../lib/agent-api";

export function useConversations() {
	const [conversations, setConversations] = useState<Conversation[]>([]);
	const [activeId, setActiveId] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await listConversations();
			setConversations(data);
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to load conversations");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	const create = useCallback(
		async (title: string) => {
			try {
				await createNewConversation();
				const id = crypto.randomUUID();
				await saveConversation(id, title);
				await refresh();
				setActiveId(id);
				return id;
			} catch (e) {
				setError(e instanceof Error ? e.message : "Failed to create conversation");
				throw e;
			}
		},
		[refresh]
	);

	const save = useCallback(
		async (id: string, title: string) => {
			try {
				await saveConversation(id, title);
				await refresh();
			} catch (e) {
				setError(e instanceof Error ? e.message : "Failed to save conversation");
				throw e;
			}
		},
		[refresh]
	);

	const load = useCallback(async (id: string) => {
		setLoading(true);
		try {
			const result = await loadConversation(id);
			setActiveId(id);
			return result;
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to load conversation");
			throw e;
		} finally {
			setLoading(false);
		}
	}, []);

	const remove = useCallback(
		async (id: string) => {
			try {
				await deleteConversation(id);
				setConversations((prev) => prev.filter((c) => c.id !== id));
				if (activeId === id) setActiveId(null);
			} catch (e) {
				setError(e instanceof Error ? e.message : "Failed to delete conversation");
				throw e;
			}
		},
		[activeId]
	);

	const activate = useCallback((id: string | null) => {
		setActiveId(id);
	}, []);

	return {
		conversations,
		activeId,
		loading,
		error,
		refresh,
		create,
		save,
		load,
		remove,
		activate,
	};
}
